import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Search, Plus, ArrowLeft, Edit2, Trash2, Save, Car,
  Battery, Wrench, FileText, Calendar, X
} from "lucide-react";

const VEHICLE_TYPES = ["Two Wheeler", "Three Wheeler", "Four Wheeler", "Commercial"];

const VehiclesPage = () => {
  const [vehicles, setVehicles] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [activeTab, setActiveTab] = useState("details");
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ total: 0, by_type: {}, by_make: {} });
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [serviceHistory, setServiceHistory] = useState([]);
  const [makes, setMakes] = useState([]);

  const fetchVehicles = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/vehicles`);
      setVehicles(r.data);
    } catch { toast.error("Failed to fetch vehicles"); }
    finally { setLoading(false); }
  }, []);

  const fetchSummary = useCallback(async () => {
    try { const r = await axios.get(`${API}/vehicles/summary`); setSummary(r.data); } catch {}
  }, []);

  const fetchMakes = useCallback(async () => {
    try { const r = await axios.get(`${API}/vehicles/makes`); setMakes(r.data); } catch {}
  }, []);

  useEffect(() => { fetchVehicles(); fetchSummary(); fetchMakes(); }, [fetchVehicles, fetchSummary, fetchMakes]);

  useEffect(() => {
    if (!search.trim()) { setFiltered(vehicles); return; }
    const q = search.toLowerCase();
    setFiltered(vehicles.filter(v =>
      (v.registration_number || "").toLowerCase().includes(q) ||
      (v.make || "").toLowerCase().includes(q) ||
      (v.model || "").toLowerCase().includes(q) ||
      (v.customer_name || "").toLowerCase().includes(q)
    ));
  }, [vehicles, search]);

  const fetchServiceHistory = useCallback(async (vehicleId) => {
    try {
      const r = await axios.get(`${API}/vehicles/${vehicleId}/service-history`);
      setServiceHistory(r.data);
    } catch { setServiceHistory([]); }
  }, []);

  const selectVehicle = (v) => {
    setSelectedVehicle(v);
    setEditing(false);
    setActiveTab("details");
    fetchServiceHistory(v.id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this vehicle?")) return;
    try {
      await axios.delete(`${API}/vehicles/${id}`);
      toast.success("Vehicle deleted");
      setSelectedVehicle(null);
      fetchVehicles();
      fetchSummary();
    } catch { toast.error("Failed to delete"); }
  };

  const startEdit = () => {
    setEditData({
      registration_number: selectedVehicle.registration_number,
      make: selectedVehicle.make, model: selectedVehicle.model,
      year: selectedVehicle.year, color: selectedVehicle.color,
      vehicle_type: selectedVehicle.vehicle_type,
      battery_type: selectedVehicle.battery_type,
      battery_capacity: selectedVehicle.battery_capacity,
      vin: selectedVehicle.vin, customer_name: selectedVehicle.customer_name,
      customer_contact: selectedVehicle.customer_contact, notes: selectedVehicle.notes,
    });
    setEditing(true);
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/vehicles/${selectedVehicle.id}`, editData);
      toast.success("Vehicle updated");
      setEditing(false);
      fetchVehicles(); fetchSummary();
      const r = await axios.get(`${API}/vehicles/${selectedVehicle.id}`);
      setSelectedVehicle(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update"); }
  };

  const getTypeBadge = (type) => {
    const map = {
      "Two Wheeler": { bg: "bg-[#EFF6FF]", text: "text-[#2563EB]" },
      "Three Wheeler": { bg: "bg-[#FEF3C7]", text: "text-[#D97706]" },
      "Four Wheeler": { bg: "bg-[#F0FDF4]", text: "text-[#16A34A]" },
      "Commercial": { bg: "bg-[#FDF2F8]", text: "text-[#DB2777]" },
    };
    return map[type] || { bg: "bg-[#F1F5F9]", text: "text-[#64748B]" };
  };

  return (
    <div className="flex h-[calc(100vh-60px)]">
      {/* Left sidebar - vehicle list */}
      <div className="w-[280px] border-r border-[#E2E8F0] flex flex-col bg-white shrink-0">
        <div className="p-3 space-y-2 border-b border-[#E2E8F0]">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#94A3B8]" />
            <Input data-testid="vehicle-search" placeholder="Search vehicles..." value={search}
              onChange={(e) => setSearch(e.target.value)} className="pl-8 h-9 text-[12px] bg-[#F8FAFC] border-[#E2E8F0]" />
          </div>
          <Button data-testid="add-vehicle-btn" onClick={() => setCreateModalOpen(true)}
            className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[11px] h-8 gap-1 font-semibold rounded-lg">
            <Plus className="w-3 h-3" /> Add Vehicle
          </Button>
        </div>

        {/* Summary strip */}
        <div className="px-3 py-2 bg-[#F8FAFC] border-b border-[#E2E8F0] flex justify-between text-[10px]">
          <span className="text-[#64748B]">Total: <b className="text-[#1E293B]">{summary.total}</b></span>
          {Object.entries(summary.by_type || {}).slice(0, 2).map(([k, v]) => (
            <span key={k} className="text-[#64748B]">{k.replace(" Wheeler", "W")}: <b>{v}</b></span>
          ))}
        </div>

        {/* Vehicle list */}
        <div className="flex-1 overflow-y-auto">
          {loading ? <p className="text-center py-8 text-[12px] text-[#94A3B8]">Loading...</p> :
           filtered.length === 0 ? (
             <div className="text-center py-12 px-4">
               <Car className="w-10 h-10 mx-auto mb-2 text-[#CBD5E1]" />
               <p className="text-[12px] text-[#94A3B8]">{search ? "No vehicles match your search" : "No vehicles yet"}</p>
               {!search && <p className="text-[11px] text-[#CBD5E1] mt-1">Add your first vehicle to get started</p>}
             </div>
           ) :
           filtered.map(v => {
            const badge = getTypeBadge(v.vehicle_type);
            const isActive = selectedVehicle?.id === v.id;
            return (
              <button key={v.id} data-testid={`vehicle-${v.id}`} onClick={() => selectVehicle(v)}
                className={`w-full text-left px-3 py-2.5 border-b border-[#F1F5F9] transition-colors ${isActive ? "bg-[#EFF6FF] border-l-[3px] border-l-[#3B82F6]" : "hover:bg-[#F8FAFC] border-l-[3px] border-l-transparent"}`}>
                <div className="flex items-center justify-between">
                  <p className={`text-[12px] font-bold tracking-wide ${isActive ? "text-[#1D4ED8]" : "text-[#1E293B]"}`}>{v.registration_number}</p>
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${badge.bg} ${badge.text}`}>{v.vehicle_type}</span>
                </div>
                <p className="text-[11px] text-[#475569] mt-0.5">{v.make} {v.model}</p>
                {v.customer_name && <p className="text-[10px] text-[#94A3B8] mt-0.5">{v.customer_name}</p>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {selectedVehicle ? (
          <>
            {/* Header */}
            <div className="border-b border-[#E2E8F0] px-6 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button data-testid="back-to-vehicles" onClick={() => setSelectedVehicle(null)} className="p-1 rounded hover:bg-[#F1F5F9]">
                    <ArrowLeft className="w-5 h-5 text-[#64748B]" />
                  </button>
                  <div>
                    <h1 className="text-[18px] font-bold text-[#1E293B]">{selectedVehicle.registration_number}</h1>
                    <p className="text-[12px] text-[#94A3B8]">{selectedVehicle.make} {selectedVehicle.model} {selectedVehicle.year ? `(${selectedVehicle.year})` : ""}</p>
                  </div>
                  {(() => { const b = getTypeBadge(selectedVehicle.vehicle_type); return <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${b.bg} ${b.text}`}>{selectedVehicle.vehicle_type}</span>; })()}
                </div>
                <div className="flex items-center gap-2">
                  {editing ? (
                    <>
                      <Button variant="outline" size="sm" onClick={() => setEditing(false)} className="text-[12px] gap-1.5 border-[#E2E8F0] text-[#475569]">
                        <X className="w-3.5 h-3.5" /> Cancel
                      </Button>
                      <Button data-testid="save-vehicle-btn" size="sm" onClick={handleSave}
                        className="bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[12px] gap-1.5 font-semibold">
                        <Save className="w-3.5 h-3.5" /> Save
                      </Button>
                    </>
                  ) : (
                    <Button data-testid="edit-vehicle-btn" variant="outline" size="sm" onClick={startEdit}
                      className="text-[12px] gap-1.5 border-[#E2E8F0] text-[#475569]">
                      <Edit2 className="w-3.5 h-3.5" /> Edit
                    </Button>
                  )}
                  <button data-testid="delete-vehicle-btn" onClick={() => handleDelete(selectedVehicle.id)}
                    className="p-1.5 rounded hover:bg-[#FEF2F2] text-[#DC2626]">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-[#E2E8F0] px-6">
              {[{ id: "details", label: "Vehicle Details" }, { id: "service", label: "Service History" }].map(tab => (
                <button key={tab.id} data-testid={`vehicle-tab-${tab.id}`} onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-3 text-[13px] font-medium border-b-2 transition-colors ${activeTab === tab.id ? "border-[#3B82F6] text-[#3B82F6]" : "border-transparent text-[#94A3B8] hover:text-[#475569]"}`}>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="flex-1 overflow-y-auto p-6">
              {activeTab === "details" && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <div className="bg-white rounded-2xl border border-[#F1F5F9] shadow-sm">
                    <div className="px-5 py-3 border-b border-[#F1F5F9] flex items-center gap-2">
                      <Car className="w-4 h-4 text-[#64748B]" />
                      <span className="text-[14px] font-bold text-[#1E293B]">Vehicle Information</span>
                    </div>
                    <div className="px-5 py-4 grid grid-cols-2 gap-x-8 gap-y-4">
                      {editing ? (
                        <>
                          <div><Label className="text-[11px] text-[#94A3B8]">Registration No. *</Label><Input data-testid="edit-reg" value={editData.registration_number} onChange={e => setEditData({...editData, registration_number: e.target.value})} className="h-9 mt-1 text-[13px]" /></div>
                          <div>
                            <Label className="text-[11px] text-[#94A3B8]">Make</Label>
                            <Select value={editData.make} onValueChange={v => setEditData({...editData, make: v})}>
                              <SelectTrigger className="h-9 mt-1 text-[13px]"><SelectValue placeholder="Select make" /></SelectTrigger>
                              <SelectContent>{makes.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div><Label className="text-[11px] text-[#94A3B8]">Model</Label><Input value={editData.model} onChange={e => setEditData({...editData, model: e.target.value})} className="h-9 mt-1 text-[13px]" /></div>
                          <div><Label className="text-[11px] text-[#94A3B8]">Year</Label><Input type="number" value={editData.year || ""} onChange={e => setEditData({...editData, year: e.target.value ? Number(e.target.value) : null})} className="h-9 mt-1 text-[13px]" /></div>
                          <div><Label className="text-[11px] text-[#94A3B8]">Color</Label><Input value={editData.color} onChange={e => setEditData({...editData, color: e.target.value})} className="h-9 mt-1 text-[13px]" /></div>
                          <div>
                            <Label className="text-[11px] text-[#94A3B8]">Vehicle Type</Label>
                            <Select value={editData.vehicle_type} onValueChange={v => setEditData({...editData, vehicle_type: v})}>
                              <SelectTrigger className="h-9 mt-1 text-[13px]"><SelectValue /></SelectTrigger>
                              <SelectContent>{VEHICLE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div><Label className="text-[11px] text-[#94A3B8]">VIN</Label><Input value={editData.vin} onChange={e => setEditData({...editData, vin: e.target.value})} className="h-9 mt-1 text-[13px]" /></div>
                        </>
                      ) : (
                        <>
                          <Field label="Registration Number" value={selectedVehicle.registration_number} />
                          <Field label="Make" value={selectedVehicle.make || "-"} />
                          <Field label="Model" value={selectedVehicle.model || "-"} />
                          <Field label="Year" value={selectedVehicle.year || "-"} />
                          <Field label="Color" value={selectedVehicle.color || "-"} />
                          <Field label="Vehicle Type" value={selectedVehicle.vehicle_type || "-"} />
                          <Field label="VIN" value={selectedVehicle.vin || "-"} />
                        </>
                      )}
                    </div>
                  </div>

                  <div className="space-y-5">
                    <div className="bg-white rounded-2xl border border-[#F1F5F9] shadow-sm">
                      <div className="px-5 py-3 border-b border-[#F1F5F9] flex items-center gap-2">
                        <Battery className="w-4 h-4 text-[#64748B]" />
                        <span className="text-[14px] font-bold text-[#1E293B]">Battery Details</span>
                      </div>
                      <div className="px-5 py-4 grid grid-cols-2 gap-x-8 gap-y-4">
                        {editing ? (
                          <>
                            <div><Label className="text-[11px] text-[#94A3B8]">Battery Type</Label><Input value={editData.battery_type} onChange={e => setEditData({...editData, battery_type: e.target.value})} className="h-9 mt-1 text-[13px]" placeholder="Li-ion, LFP..." /></div>
                            <div><Label className="text-[11px] text-[#94A3B8]">Battery Capacity</Label><Input value={editData.battery_capacity} onChange={e => setEditData({...editData, battery_capacity: e.target.value})} className="h-9 mt-1 text-[13px]" placeholder="3.97 kWh" /></div>
                          </>
                        ) : (
                          <>
                            <Field label="Battery Type" value={selectedVehicle.battery_type || "-"} />
                            <Field label="Battery Capacity" value={selectedVehicle.battery_capacity || "-"} />
                          </>
                        )}
                      </div>
                    </div>

                    <div className="bg-white rounded-2xl border border-[#F1F5F9] shadow-sm">
                      <div className="px-5 py-3 border-b border-[#F1F5F9] flex items-center gap-2">
                        <FileText className="w-4 h-4 text-[#64748B]" />
                        <span className="text-[14px] font-bold text-[#1E293B]">Owner Details</span>
                      </div>
                      <div className="px-5 py-4 grid grid-cols-2 gap-x-8 gap-y-4">
                        {editing ? (
                          <>
                            <div><Label className="text-[11px] text-[#94A3B8]">Customer Name</Label><Input value={editData.customer_name} onChange={e => setEditData({...editData, customer_name: e.target.value})} className="h-9 mt-1 text-[13px]" /></div>
                            <div><Label className="text-[11px] text-[#94A3B8]">Contact</Label><Input value={editData.customer_contact} onChange={e => setEditData({...editData, customer_contact: e.target.value})} className="h-9 mt-1 text-[13px]" /></div>
                            <div className="col-span-2"><Label className="text-[11px] text-[#94A3B8]">Notes</Label><Textarea value={editData.notes} onChange={e => setEditData({...editData, notes: e.target.value})} className="mt-1 text-[13px]" rows={2} /></div>
                          </>
                        ) : (
                          <>
                            <Field label="Customer Name" value={selectedVehicle.customer_name || "-"} />
                            <Field label="Contact" value={selectedVehicle.customer_contact || "-"} />
                            <div className="col-span-2"><Field label="Notes" value={selectedVehicle.notes || "-"} /></div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "service" && (
                <div className="bg-white rounded-2xl border border-[#F1F5F9] shadow-sm">
                  <div className="px-5 py-3 border-b border-[#F1F5F9] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Wrench className="w-4 h-4 text-[#64748B]" />
                      <span className="text-[14px] font-bold text-[#1E293B]">Service History</span>
                    </div>
                    <span className="text-[11px] text-[#94A3B8]">{serviceHistory.length} record{serviceHistory.length !== 1 ? "s" : ""}</span>
                  </div>
                  {serviceHistory.length === 0 ? (
                    <div className="text-center py-12">
                      <Wrench className="w-10 h-10 mx-auto mb-2 text-[#CBD5E1]" />
                      <p className="text-[13px] text-[#94A3B8]">No service records yet</p>
                      <p className="text-[11px] text-[#CBD5E1] mt-1">Job cards linked to this vehicle will appear here</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-[#F1F5F9]">
                      {serviceHistory.map((sh, idx) => (
                        <div key={idx} className="px-5 py-3 flex items-center justify-between hover:bg-[#F8FAFC] transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-[#EFF6FF] flex items-center justify-center shrink-0">
                              <FileText className="w-3.5 h-3.5 text-[#3B82F6]" />
                            </div>
                            <div>
                              <p className="text-[13px] font-semibold text-[#1E293B]">{sh.job_card_number}</p>
                              <p className="text-[11px] text-[#94A3B8]">{sh.type_of_service}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <StatusBadge status={sh.status} />
                            <div className="flex items-center gap-1 mt-1 text-[10px] text-[#94A3B8]">
                              <Calendar className="w-3 h-3" />
                              {sh.submitted_date || "-"}
                            </div>
                          </div>
                          <div className="text-right ml-4">
                            {sh.invoice_number ? (
                              <div>
                                <p className="text-[11px] font-medium text-[#1E293B]">{sh.invoice_number}</p>
                                <p className={`text-[10px] font-bold ${sh.invoice_status === "Paid" ? "text-[#16A34A]" : "text-[#D97706]"}`}>
                                  {sh.invoice_amount != null ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(sh.invoice_amount) : ""} · {sh.invoice_status}
                                </p>
                              </div>
                            ) : (
                              <span className="text-[10px] text-[#CBD5E1]">No Invoice</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          /* No vehicle selected — overview */
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-[22px] font-bold text-[#1E293B]">Vehicles</h1>
              <p className="text-[13px] text-[#94A3B8]">Select a vehicle from the list to view details</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <SummaryCard icon={Car} iconBg="bg-[#EFF6FF]" iconColor="text-[#3B82F6]" label="Total Vehicles" value={summary.total} />
              {Object.entries(summary.by_type || {}).slice(0, 3).map(([k, v]) => (
                <SummaryCard key={k} icon={Car} iconBg="bg-[#F0FDF4]" iconColor="text-[#16A34A]" label={k} value={v} />
              ))}
            </div>
            {Object.keys(summary.by_make || {}).length > 0 && (
              <div className="bg-white rounded-2xl border border-[#F1F5F9] shadow-sm p-5">
                <h3 className="text-[14px] font-bold text-[#1E293B] mb-3">By Manufacturer</h3>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(summary.by_make || {}).sort((a, b) => b[1] - a[1]).map(([make, count]) => (
                    <span key={make} className="px-3 py-1.5 rounded-full bg-[#F8FAFC] border border-[#E2E8F0] text-[12px] font-medium text-[#475569]">
                      {make} <b className="text-[#1E293B] ml-1">{count}</b>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Vehicle Modal */}
      <CreateVehicleModal open={createModalOpen} makes={makes}
        onClose={(refresh) => { setCreateModalOpen(false); if (refresh) { fetchVehicles(); fetchSummary(); }}} />
    </div>
  );
};

const Field = ({ label, value }) => (
  <div>
    <p className="text-[11px] text-[#94A3B8] mb-0.5">{label}</p>
    <p className="text-[13px] font-medium text-[#1E293B]">{value}</p>
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    "Pending": "bg-[#FEF3C7] text-[#D97706]",
    "In Progress": "bg-[#DBEAFE] text-[#2563EB]",
    "Completed": "bg-[#D1FAE5] text-[#059669]",
    "Delivered": "bg-[#F0FDF4] text-[#16A34A]",
  };
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${map[status] || "bg-[#F1F5F9] text-[#64748B]"}`}>{status}</span>;
};

const SummaryCard = ({ icon: Icon, iconBg, iconColor, label, value }) => (
  <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#F1F5F9]">
    <div className="flex items-center gap-2 mb-2">
      <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center`}>
        <Icon className={`w-4 h-4 ${iconColor}`} />
      </div>
    </div>
    <p className="text-[11px] text-[#94A3B8]">{label}</p>
    <p className="text-[28px] font-bold text-[#1E293B]">{value}</p>
  </div>
);

const CreateVehicleModal = ({ open, onClose, makes }) => {
  const [data, setData] = useState({
    registration_number: "", make: "", model: "", year: "", color: "",
    vehicle_type: "Two Wheeler", battery_type: "", battery_capacity: "",
    vin: "", customer_name: "", customer_contact: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setData(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (open) setData({
      registration_number: "", make: "", model: "", year: "", color: "",
      vehicle_type: "Two Wheeler", battery_type: "", battery_capacity: "",
      vin: "", customer_name: "", customer_contact: "", notes: "",
    });
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!data.registration_number.trim()) { toast.error("Registration number is required"); return; }
    setSaving(true);
    try {
      const payload = { ...data, year: data.year ? Number(data.year) : null };
      await axios.post(`${API}/vehicles`, payload);
      toast.success("Vehicle added");
      onClose(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add vehicle"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="create-vehicle-modal">
        <DialogHeader>
          <DialogTitle className="text-[16px]">Add New Vehicle</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px]">Registration No. *</Label>
              <Input data-testid="new-vehicle-reg" value={data.registration_number}
                onChange={e => set("registration_number", e.target.value)} className="h-9 mt-1 text-[13px]" placeholder="OD-02-AB-1234" required />
            </div>
            <div>
              <Label className="text-[11px]">Vehicle Type</Label>
              <Select value={data.vehicle_type} onValueChange={v => set("vehicle_type", v)}>
                <SelectTrigger data-testid="new-vehicle-type" className="h-9 mt-1 text-[13px]"><SelectValue /></SelectTrigger>
                <SelectContent>{VEHICLE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Make</Label>
              <Select value={data.make} onValueChange={v => set("make", v)}>
                <SelectTrigger data-testid="new-vehicle-make" className="h-9 mt-1 text-[13px]"><SelectValue placeholder="Select make" /></SelectTrigger>
                <SelectContent>{makes.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-[11px]">Model</Label><Input data-testid="new-vehicle-model" value={data.model} onChange={e => set("model", e.target.value)} className="h-9 mt-1 text-[13px]" placeholder="S1 Pro" /></div>
            <div><Label className="text-[11px]">Year</Label><Input type="number" value={data.year} onChange={e => set("year", e.target.value)} className="h-9 mt-1 text-[13px]" placeholder="2024" /></div>
            <div><Label className="text-[11px]">Color</Label><Input value={data.color} onChange={e => set("color", e.target.value)} className="h-9 mt-1 text-[13px]" placeholder="Matte Black" /></div>
            <div><Label className="text-[11px]">Battery Type</Label><Input value={data.battery_type} onChange={e => set("battery_type", e.target.value)} className="h-9 mt-1 text-[13px]" placeholder="Li-ion" /></div>
            <div><Label className="text-[11px]">Battery Capacity</Label><Input value={data.battery_capacity} onChange={e => set("battery_capacity", e.target.value)} className="h-9 mt-1 text-[13px]" placeholder="3.97 kWh" /></div>
            <div><Label className="text-[11px]">VIN</Label><Input value={data.vin} onChange={e => set("vin", e.target.value)} className="h-9 mt-1 text-[13px]" /></div>
            <div><Label className="text-[11px]">Customer Name</Label><Input value={data.customer_name} onChange={e => set("customer_name", e.target.value)} className="h-9 mt-1 text-[13px]" /></div>
            <div><Label className="text-[11px]">Customer Contact</Label><Input value={data.customer_contact} onChange={e => set("customer_contact", e.target.value)} className="h-9 mt-1 text-[13px]" placeholder="9876543210" /></div>
          </div>
          <div><Label className="text-[11px]">Notes</Label><Textarea value={data.notes} onChange={e => set("notes", e.target.value)} className="mt-1 text-[13px]" rows={2} /></div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onClose(false)} className="text-[12px]">Cancel</Button>
            <Button data-testid="submit-vehicle-btn" type="submit" disabled={saving} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[12px]">{saving ? "Adding..." : "Add Vehicle"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default VehiclesPage;
